import {
  ChainRegistryEntry,
  getChainByAlias,
  getChainByChainId,
  listChainRegistryEntries,
  onChainRegistryUpdate,
} from '@shared/chainRegistryClient';
import { buildExplorerEntityUrl } from '@shared/explorerUrls';

export interface ChainConfig {
  chainId: string;
  numericChainId: number;
  name: string;
  shortName: string;
  network: 'mainnet' | 'testnet' | 'unknown';
  prefix: string;
  rpcUrl?: string;
  explorerUrl?: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

export type ChainFormatType = 'full' | 'short' | 'withId' | 'id';
export type NetworkFilter = 'mainnet' | 'testnet' | 'all';

export let CHAIN_CONFIGS: ChainConfig[] = [];
export const CHAIN_CONFIG_BY_NUMERIC_ID = new Map<number, ChainConfig>();
export const CHAIN_CONFIG_BY_CHAIN_ID = new Map<string, ChainConfig>();
export const CHAIN_CONFIG_BY_PREFIX_AND_ID = new Map<string, ChainConfig>();

export const DEFAULT_CHAIN_CONFIG: ChainConfig = {
  chainId: 'unknown',
  numericChainId: 0,
  name: 'Unknown Chain',
  shortName: 'Unknown',
  network: 'unknown',
  prefix: 'unknown',
  nativeCurrency: {
    name: 'Unknown',
    symbol: 'UNKNOWN',
    decimals: 18,
  },
};

export const SUPPORTED_PREFIXES = ['eth', 'bsc', 'base', 'polygon', 'tron', 'solana'] as const;
export type ChainPrefix = typeof SUPPORTED_PREFIXES[number];

const SHORT_NAME_OVERRIDES: Record<string, { mainnet: string; testnet: string }> = {
  bsc: {
    mainnet: 'BNB Smart Chain',
    testnet: 'BNB Smart Chain Testnet',
  },
  polygon: {
    mainnet: 'POLYGON',
    testnet: 'POLYGON-AMOY',
  },
  base: {
    mainnet: 'BASE',
    testnet: 'BASE-SEPOLIA',
  },
  ethereum: {
    mainnet: 'ETHEREUM',
    testnet: 'ETH-SEPOLIA',
  },
  tron: {
    mainnet: 'TRON',
    testnet: 'TRON-NILE',
  },
  solana: {
    mainnet: 'SOLANA',
    testnet: 'SOLANA-DEVNET',
  },
};

const PREFIX_OVERRIDES: Record<string, string> = {
  ethereum: 'eth',
};

function sortConfigs(configs: ChainConfig[]): ChainConfig[] {
  return configs
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
}

function derivePrefix(entry: ChainRegistryEntry): string {
  const override = PREFIX_OVERRIDES[entry.key];
  if (override) {
    return override;
  }
  return entry.key;
}

function deriveShortName(entry: ChainRegistryEntry): string {
  const override = SHORT_NAME_OVERRIDES[entry.key];
  if (!override) {
    return entry.name;
  }
  return entry.isTestnet ? override.testnet : override.mainnet;
}

export function buildChainConfigFromRegistryEntry(entry: ChainRegistryEntry): ChainConfig {
  const prefix = derivePrefix(entry);
  const explorerUrl = entry.scanBaseUrl ?? entry.blockExplorerUrl ?? undefined;
  if (!entry.nativeCurrency) {
    throw new Error(`Chain ${entry.id} is missing native currency metadata`);
  }
  const nativeCurrency = entry.nativeCurrency;

  return {
    chainId: `${prefix}${entry.chainId}`,
    numericChainId: entry.chainId,
    name: entry.name,
    shortName: deriveShortName(entry),
    network: entry.isTestnet ? 'testnet' : 'mainnet',
    prefix,
    explorerUrl,
    nativeCurrency,
  };
}

function upsertChainConfig(config: ChainConfig): ChainConfig {
  CHAIN_CONFIG_BY_NUMERIC_ID.set(config.numericChainId, config);
  CHAIN_CONFIG_BY_CHAIN_ID.set(config.chainId.toLowerCase(), config);
  CHAIN_CONFIG_BY_PREFIX_AND_ID.set(`${config.prefix}${config.numericChainId}`, config);

  const filtered = CHAIN_CONFIGS.filter(existing => existing.numericChainId !== config.numericChainId);
  CHAIN_CONFIGS = sortConfigs([...filtered, config]);
  return config;
}

function ensureChainConfigFromEntry(entry: ChainRegistryEntry): ChainConfig {
  const config = buildChainConfigFromRegistryEntry(entry);
  return upsertChainConfig(config);
}

function rebuildChainIndexes(entries: ChainRegistryEntry[]): void {
  const configs = sortConfigs(entries.map(buildChainConfigFromRegistryEntry));

  CHAIN_CONFIGS = configs;
  CHAIN_CONFIG_BY_NUMERIC_ID.clear();
  CHAIN_CONFIG_BY_CHAIN_ID.clear();
  CHAIN_CONFIG_BY_PREFIX_AND_ID.clear();

  configs.forEach(config => {
    CHAIN_CONFIG_BY_NUMERIC_ID.set(config.numericChainId, config);
    CHAIN_CONFIG_BY_CHAIN_ID.set(config.chainId.toLowerCase(), config);
    CHAIN_CONFIG_BY_PREFIX_AND_ID.set(`${config.prefix}${config.numericChainId}`, config);
  });
}

function initializeChainConfigs(): void {
  const snapshot = listChainRegistryEntries();
  rebuildChainIndexes(snapshot);
}

initializeChainConfigs();

onChainRegistryUpdate(entries => {
  rebuildChainIndexes(entries);
});

function normalizeInput(input: string | number | null | undefined): string | null {
  if (input === null || input === undefined) {
    return null;
  }
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      return null;
    }
    return input.toString();
  }
  const value = input.toString().trim();
  return value.length === 0 ? null : value;
}

function collapsePrefixPattern(value: string): string {
  return value.replace(/[^a-z0-9]/gu, '');
}

export function parseChainId(input: string | number | null | undefined): string {
  const normalized = normalizeInput(input);
  if (!normalized) {
    return '0';
  }

  const numericString = normalized.match(/^(\d+)$/u);
  if (numericString) {
    return numericString[1];
  }

  if (normalized.startsWith('chn_')) {
    const parts = normalized.split('_');
    const lastPart = parts[parts.length - 1] ?? '';
    const digits = lastPart.match(/\d+$/u);
    if (digits) {
      return digits[0];
    }
  }

  const lower = normalized.toLowerCase();
  const aliasEntry =
    getChainByAlias(lower) ||
    getChainByAlias(normalized) ||
    getChainByAlias(collapsePrefixPattern(lower));
  if (aliasEntry) {
    return aliasEntry.chainId.toString();
  }

  const prefixMatch = SUPPORTED_PREFIXES.find(prefix => lower.startsWith(prefix));
  if (prefixMatch) {
    const suffix = lower.slice(prefixMatch.length);
    if (/^\d+$/u.test(suffix)) {
      return suffix;
    }
  }

  const chainIdEntry = getChainByChainId(normalized);
  if (chainIdEntry) {
    return chainIdEntry.chainId.toString();
  }

  return lower;
}

export function extractNumericChainId(input: string | number | null | undefined): number {
  const parsed = parseInt(parseChainId(input), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getChainInfo(input: string | number | null | undefined): ChainConfig {
  const normalized = normalizeInput(input);
  if (!normalized) {
    return DEFAULT_CHAIN_CONFIG;
  }

  const numericId = extractNumericChainId(normalized);
  if (numericId > 0) {
    const byNumeric = CHAIN_CONFIG_BY_NUMERIC_ID.get(numericId);
    if (byNumeric) {
      return byNumeric;
    }
  }

  const lower = normalized.toLowerCase();
  const collapsed = collapsePrefixPattern(lower);

  const byChainId = CHAIN_CONFIG_BY_CHAIN_ID.get(lower);
  if (byChainId) {
    return byChainId;
  }

  const byPrefix = CHAIN_CONFIG_BY_PREFIX_AND_ID.get(collapsed);
  if (byPrefix) {
    return byPrefix;
  }

  let registryEntry: ChainRegistryEntry | undefined;

  if (numericId > 0) {
    registryEntry = getChainByChainId(numericId) ?? getChainByChainId(numericId.toString());
  }

  registryEntry =
    registryEntry ??
    getChainByAlias(lower, numericId > 0 ? numericId : undefined) ??
    getChainByAlias(lower) ??
    getChainByAlias(normalized);

  if (registryEntry) {
    return ensureChainConfigFromEntry(registryEntry);
  }

  return DEFAULT_CHAIN_CONFIG;
}

export function formatChainName(
  input: string | number | null | undefined,
  format: ChainFormatType = 'full',
): string {
  const chain = getChainInfo(input);

  switch (format) {
    case 'short':
      return chain.shortName;
    case 'withId':
      return `${chain.shortName} (${chain.numericChainId})`;
    case 'id':
      return chain.numericChainId.toString();
    default:
      return chain.shortName !== 'Unknown' ? chain.shortName : chain.name;
  }
}

export function isTestnet(input: string | number | null | undefined): boolean {
  return getChainInfo(input).network === 'testnet';
}

export function isMainnet(input: string | number | null | undefined): boolean {
  return getChainInfo(input).network === 'mainnet';
}

export function buildChainId(prefix: ChainPrefix, numericId: number): string {
  return `${prefix}${numericId}`;
}

export function getChainPrefix(
  input: string | number | null | undefined,
): ChainPrefix | 'unknown' {
  const prefix = getChainInfo(input).prefix;
  if (SUPPORTED_PREFIXES.includes(prefix as ChainPrefix)) {
    return prefix as ChainPrefix;
  }
  return 'unknown';
}

export function getAllChains(): ChainConfig[] {
  return [...CHAIN_CONFIGS];
}

export function getChainsByNetwork(network: NetworkFilter): ChainConfig[] {
  if (network === 'all') {
    return getAllChains();
  }
  return CHAIN_CONFIGS.filter(config => config.network === network);
}

export function getChainsByPrefix(prefix: ChainPrefix): ChainConfig[] {
  return CHAIN_CONFIGS.filter(config => config.prefix === prefix);
}

export function isChainSupported(input: string | number | null | undefined): boolean {
  if (input === null || input === undefined) {
    return false;
  }
  const chain = getChainInfo(input);
  return chain !== DEFAULT_CHAIN_CONFIG && chain.numericChainId !== 0;
}

export function getExplorerUrl(
  input: string | number | null | undefined,
): string | undefined {
  return getChainInfo(input).explorerUrl;
}

export function getExplorerTxUrl(
  input: string | number | null | undefined,
  txHash: string,
): string | undefined {
  const baseUrl = getExplorerUrl(input);
  return buildExplorerEntityUrl(baseUrl, 'tx', txHash);
}

export function getExplorerAddressUrl(
  input: string | number | null | undefined,
  address: string,
): string | undefined {
  const baseUrl = getExplorerUrl(input);
  return buildExplorerEntityUrl(baseUrl, 'address', address);
}

export function parseFromPrefixAndId(
  prefix: string,
  numericId: string | number,
): ChainConfig {
  const cleanPrefix = prefix.trim().toLowerCase();
  const cleanNumericId = typeof numericId === 'number' ? numericId : parseInt(String(numericId), 10);

  if (!cleanPrefix || Number.isNaN(cleanNumericId)) {
    return DEFAULT_CHAIN_CONFIG;
  }

  const lookupKey = `${cleanPrefix}${cleanNumericId}`;
  const cached = CHAIN_CONFIG_BY_PREFIX_AND_ID.get(lookupKey);
  if (cached) {
    return cached;
  }

  const entry =
    getChainByAlias(cleanPrefix, cleanNumericId) ??
    getChainByAlias(lookupKey, cleanNumericId) ??
    getChainByChainId(cleanNumericId);

  if (entry) {
    return ensureChainConfigFromEntry(entry);
  }

  return DEFAULT_CHAIN_CONFIG;
}
